/**
 * The rental half of a deployment draft, and its persistence.
 *
 * Phase 4 step 5 of THREE-PATH-RENTAL-IMPLEMENTATION-PLAN.md: persist the
 * rental mode, provider, host-offer id, quote id/expiry and pricing source, and
 * "sign the binding rather than displaying an unenforced host".
 *
 * The draft lives in localStorage, which means a tenant can close the tab and
 * come back — so the load path has to answer a question the save path never
 * asks: is this quote still real? Everything awkward in this module comes from
 * that, and it is the reason `loadRentalDraft` demands a chain height instead
 * of politely defaulting.
 */

import {
  RENTAL_PATHS,
  isHostProduct,
  isQuoteLive,
  resolveMode,
  resolvePricingSource,
  type DirectQuoteView,
  type PricingSource,
  type RentalMode,
  type RentalPath,
} from './rental'

/** The rental fields carried alongside the guided draft. */
export interface RentalDraft {
  /** The path the tenant chose. */
  path: RentalPath
  /**
   * The mode implied by the path and the tenant's price.
   *
   * Stored as well as derivable, because it is what gets signed and what a
   * later screen reports back to the tenant. A stored value that disagrees
   * with the derivation is a bug, and `normalizeRentalDraft` resolves it in
   * favour of the derivation — see there for why that direction.
   */
  mode: RentalMode
  pricingSource: PricingSource
  /** The provider whose quote or offer this is. */
  provider?: string | undefined
  /** The opaque host offer id, for a host product. */
  hostOfferId?: string | undefined
  /** The live quote, if one has been fetched and is still good. */
  quote?: DirectQuoteView | undefined
}

export const DEFAULT_RENTAL_DRAFT: RentalDraft = {
  // Competitive is the default because it is the only path that promises
  // nothing a provider has to honour. A tenant who lands mid-flow through a
  // stale link, a restored tab or a bug gets the request that cannot
  // misrepresent itself, rather than one claiming a host and a fixed price
  // nobody agreed to.
  path: 'competitive',
  mode: 'RENTAL_MODE_COMPETITIVE',
  pricingSource: 'tenant-cap',
}

function isRentalPath(value: unknown): value is RentalPath {
  return typeof value === 'string' && (RENTAL_PATHS as readonly string[]).includes(value)
}

function normalizeQuote(raw: unknown): DirectQuoteView | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const q = raw as Partial<DirectQuoteView>

  // Every field is required. A partial quote is not a weaker quote — it is one
  // whose missing half a screen would render as blank or zero, and a review
  // screen showing "0 uddt/block" for a real commitment is worse than showing
  // no quote at all.
  if (
    typeof q.quoteId !== 'string' || q.quoteId === '' ||
    typeof q.provider !== 'string' || q.provider === '' ||
    typeof q.rentalMode !== 'string' ||
    typeof q.pricePerBlock !== 'string' || !/^\d+$/.test(q.pricePerBlock) ||
    typeof q.expiryHeight !== 'number' || !Number.isFinite(q.expiryHeight) || q.expiryHeight <= 0
  ) {
    return undefined
  }

  return {
    quoteId: q.quoteId,
    provider: q.provider,
    hostOfferId: typeof q.hostOfferId === 'string' ? q.hostOfferId : '',
    rentalMode: q.rentalMode as RentalMode,
    pricePerBlock: q.pricePerBlock,
    expiryHeight: q.expiryHeight,
  }
}

/**
 * Normalize a persisted rental draft, given the tenant's current price.
 *
 * The price is a parameter because the mode depends on it and the two are
 * stored separately — the guided draft owns `priceCap`, this module owns the
 * rest. Passing it in means the derivation cannot drift from what the tenant
 * actually typed.
 *
 * When the stored mode disagrees with the derivation, the DERIVATION wins.
 * The stored value is a snapshot of a past decision; the derivation reflects
 * the price the tenant has now. Trusting the snapshot would let a tenant who
 * typed a maximum into a restored host-rental draft keep a direct mode — the
 * exact blur the domain model exists to prevent.
 */
export function normalizeRentalDraft(raw: unknown, priceCap: string): RentalDraft {
  const parsed = (typeof raw === 'object' && raw !== null ? raw : {}) as Partial<RentalDraft>

  const path = isRentalPath(parsed.path) ? parsed.path : DEFAULT_RENTAL_DRAFT.path
  const provider = typeof parsed.provider === 'string' && parsed.provider !== '' ? parsed.provider : undefined
  const hostOfferId =
    typeof parsed.hostOfferId === 'string' && parsed.hostOfferId !== '' ? parsed.hostOfferId : undefined

  const mode = resolveMode({ path, priceCap, provider, hostOfferId })
  const pricingSource = resolvePricingSource({ path, priceCap, provider, hostOfferId })

  const draft: RentalDraft = { path, mode, pricingSource }
  if (provider !== undefined) draft.provider = provider

  // A host offer id is meaningless without a host product, and keeping one
  // would let a later screen render a host binding for a mode that has none —
  // the unenforced host from plan §3, restored from storage.
  if (hostOfferId !== undefined && isHostProduct(mode)) draft.hostOfferId = hostOfferId

  const quote = normalizeQuote(parsed.quote)
  // A quote for a different mode than the draft now has is not this draft's
  // quote. It survives a mode change in storage only because they are stored
  // together, and honouring it would price one product with another's quote.
  if (quote && quote.rentalMode === mode) draft.quote = quote

  return draft
}

export const rentalDraftStorageKey = (address: string): string =>
  `ddws.console.rental.${address}`

/**
 * Load a rental draft, discarding a quote that is no longer live.
 *
 * `height` is REQUIRED, and that is the point of this function existing at all.
 * A tenant can close the tab on a review screen and return an hour later; the
 * quote in storage is then a fixed price that no provider is holding and no
 * capacity is reserved for. Restoring it as live would show them that price,
 * take them into a wallet prompt, and fail on chain with an expired quote —
 * after they approved it.
 *
 * Making the height a parameter rather than an optional means a caller cannot
 * reach the convenient wrong answer. There is no `loadRentalDraft(address)`.
 */
export function loadRentalDraft(
  storage: Pick<Storage, 'getItem'>,
  address: string,
  priceCap: string,
  height: number,
): RentalDraft {
  let raw: unknown = {}
  try {
    raw = JSON.parse(storage.getItem(rentalDraftStorageKey(address)) ?? '{}')
  } catch {
    // Corrupt storage falls back to defaults rather than throwing. A tenant
    // whose draft cannot be parsed should get an empty form, not a broken
    // screen — and the default path is competitive, which promises nothing.
    raw = {}
  }

  const draft = normalizeRentalDraft(raw, priceCap)
  if (draft.quote && !isQuoteLive(draft.quote, height)) {
    const { quote: _discarded, ...rest } = draft
    return rest
  }
  return draft
}

export function saveRentalDraft(
  storage: Pick<Storage, 'setItem'>,
  address: string,
  draft: RentalDraft,
): void {
  try {
    storage.setItem(rentalDraftStorageKey(address), JSON.stringify(draft))
  } catch {
    // A full or blocked storage must not break the flow. The draft is a
    // convenience; the transaction the tenant is about to sign carries
    // everything that matters.
  }
}

/** Clear a rental draft, after a lease is created or the tenant starts over. */
export function clearRentalDraft(storage: Pick<Storage, 'removeItem'>, address: string): void {
  try {
    storage.removeItem(rentalDraftStorageKey(address))
  } catch {
    /* see saveRentalDraft */
  }
}

/**
 * What a review screen must state about this draft, per Gate 4.
 *
 * Returned as data rather than rendered here so the screen owns layout and
 * this module owns truth. Every field is non-optional: Gate 4 requires a
 * review screen to state provider binding, resource guarantee, shared or
 * exclusive status, exact price, expiry, deposit and refund behaviour, and an
 * optional field is one a screen can forget to render.
 */
export interface ReviewFacts {
  /** "This provider" or "Any eligible provider". */
  providerBinding: string
  /** "One specific machine" / "No specific machine". */
  hostBinding: string
  /** "Exclusive" / "Shared" / "Not applicable". */
  occupancy: string
  /** How the price should be read. */
  priceMeaning: string
  /** When the commitment lapses, or that there is none. */
  expiry: string
  /** What is held up front, and that it is not a fee. */
  deposit: string
  /** What comes back, and when. */
  refund: string
}

export function reviewFacts(draft: RentalDraft, height: number): ReviewFacts {
  // Deposit and refund read the same for both paths, and that is worth stating
  // rather than duplicating: the escrow mechanism is the market module's, not
  // rental's, so a direct lease's deposit behaves exactly as a competitive
  // one's. A tenant who has rented before should not have to re-learn it, and
  // wording it differently per path would imply a difference that does not
  // exist.
  const deposit = 'Held in escrow, not spent. It funds the lease and is not a fee.'
  const refund = 'Whatever the lease has not used is returned when you close it.'

  if (draft.mode === 'RENTAL_MODE_COMPETITIVE') {
    return {
      providerBinding: 'Any eligible provider may respond',
      hostBinding: 'No specific machine is reserved',
      occupancy: 'Not applicable until you accept an offer',
      priceMeaning: 'You set no maximum. Offers can arrive at any price, and you choose.',
      expiry: 'Your request stays open until you accept an offer or close it',
      deposit,
      // The one genuine difference: a competitive request that nobody bids on
      // still holds a deposit, and the tenant has to close it to get that
      // back. Saying so is the difference between an abandoned request and an
      // abandoned deposit.
      refund: `${refund} If no offer arrives, close the request to release it.`,
    }
  }

  const blocks = draft.quote ? Math.max(0, draft.quote.expiryHeight - height) : 0

  return {
    providerBinding: draft.provider ? `Only ${draft.provider}` : 'A provider must be chosen',
    hostBinding: isHostProduct(draft.mode)
      ? 'One specific machine, bound in the signed transaction'
      : 'No specific machine is reserved',
    occupancy:
      draft.mode === 'RENTAL_MODE_DIRECT_HOST_EXCLUSIVE'
        ? 'Exclusive — no other tenant runs on this machine'
        : isHostProduct(draft.mode)
          ? 'Shared — other tenants may be on this machine'
          : 'Not applicable',
    priceMeaning: 'Exactly this amount. The provider has signed it.',
    expiry: draft.quote
      ? `This price is held for ${blocks} more block${blocks === 1 ? '' : 's'}`
      : 'No price is held yet',
    deposit,
    refund,
  }
}
